import { useMemo, useState } from "react";
import {
  Plus,
  Search,
  Tag,
  ArrowDownLeft,
  ArrowUpRight,
  Trash2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { excluirCategoria, salvarCategoria } from "../services/financeService";
import "../styles/pages/categorias.css";

const iconesBasicos = [
  "🍔", "🛒", "🏠", "🚗", "💡", "📱",
  "🎮", "💰", "🏥", "✈️", "📚", "🎵",
];

const iconesExtras = [
  "🍕", "☕", "🧾", "🛍️", "💳", "🎁",
  "🏦", "💊", "🚌", "⛽", "🔧", "🧹",
  "🐶", "👶", "💇", "👕", "🏋️", "🎬",
  "🍺", "🍰", "🥩", "🥗", "🧼", "🧴",
  "📦", "🛠️", "🖥️", "📈", "📉", "💼",
  "🏪", "🧑‍💻", "🚚", "🧠", "💻", "📊",
];

export default function Categorias({ uid, categorias = [] }) {
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("despesa");
  const [icone, setIcone] = useState("🛒");
  const [busca, setBusca] = useState("");
  const [mostrarMaisIcones, setMostrarMaisIcones] = useState(false);

  const receitas = categorias.filter((cat) => cat.tipo === "receita").length;
  const despesas = categorias.filter((cat) => cat.tipo === "despesa").length;

  const iconesVisiveis = mostrarMaisIcones
    ? [...iconesBasicos, ...iconesExtras]
    : iconesBasicos;

  const categoriasFiltradas = useMemo(() => {
    return categorias.filter((cat) => {
      const texto = `${cat.nome || ""} ${cat.tipo || ""}`.toLowerCase();
      return texto.includes(busca.toLowerCase());
    });
  }, [categorias, busca]);

  async function criar(e) {
    e.preventDefault();
    if (!nome.trim()) return;

    await salvarCategoria(uid, {
      nome,
      tipo,
      icone,
    });

    setNome("");
    setTipo("despesa");
    setIcone("🛒");
    setMostrarMaisIcones(false);
  }

  return (
    <section className="cat-page">
      <div className="cat-header">
        <div>
          <span>Organização</span>
          <h2>Categorias</h2>
          <p>Organize receitas e despesas de forma inteligente.</p>
        </div>

        <div className="cat-header-icon">
          <Tag size={24} />
        </div>
      </div>

      <div className="cat-summary-grid">
        <article className="cat-summary-card blue">
          <div>
            <span>Total</span>
            <strong>{categorias.length}</strong>
          </div>

          <div className="cat-card-icon">
            <Tag size={22} />
          </div>
        </article>

        <article className="cat-summary-card green">
          <div>
            <span>Receitas</span>
            <strong>{receitas}</strong>
          </div>

          <div className="cat-card-icon">
            <ArrowUpRight size={22} />
          </div>
        </article>

        <article className="cat-summary-card red">
          <div>
            <span>Despesas</span>
            <strong>{despesas}</strong>
          </div>

          <div className="cat-card-icon">
            <ArrowDownLeft size={22} />
          </div>
        </article>
      </div>

      <section className="cat-form-card">
        <div className="cat-form-header">
          <div>
            <h2>Nova categoria</h2>
            <p>Crie categorias personalizadas para seus lançamentos.</p>
          </div>

          <div className="cat-form-icon">
            <Plus size={22} />
          </div>
        </div>

        <form className="cat-form" onSubmit={criar}>
          <div className="cat-field full">
            <label>Nome</label>

            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Mercado, salário, marketing..."
            />
          </div>

          <div className="cat-field cat-icon-field">
            <label>Ícone</label>

            <div className={mostrarMaisIcones ? "cat-icons-grid expanded" : "cat-icons-grid"}>
              {iconesVisiveis.map((item) => (
                <button
                  key={item}
                  type="button"
                  className={icone === item ? "cat-icon-option active" : "cat-icon-option"}
                  onClick={() => setIcone(item)}
                >
                  {item}
                </button>
              ))}
            </div>

            <button
              className="cat-icons-more-btn"
              type="button"
              onClick={() => setMostrarMaisIcones((atual) => !atual)}
            >
              {mostrarMaisIcones ? (
                <>
                  <ChevronUp size={17} />
                  Ver menos ícones
                </>
              ) : (
                <>
                  <ChevronDown size={17} />
                  Ver mais ícones
                </>
              )}
            </button>
          </div>

          <div className="cat-field">
            <label>Tipo</label>

            <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="receita">Receita</option>
              <option value="despesa">Despesa</option>
            </select>
          </div>

          <button className="cat-submit-btn" type="submit">
            <Plus size={18} />
            Criar categoria
          </button>
        </form>
      </section>

      <section className="cat-list-card">
        <div className="cat-list-header">
          <div>
            <h2>Minhas categorias</h2>
            <span>{categoriasFiltradas.length} categoria(s) encontrada(s).</span>
          </div>

          <label className="cat-search">
            <Search size={16} />

            <input
              placeholder="Buscar categoria..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </label>
        </div>

        <div className="cat-list">
          {categoriasFiltradas.map((cat) => (
            <article className="cat-item" key={cat.id}>
              <div className="cat-item-left">
                <div className="cat-item-icon">{cat.icone}</div>

                <div>
                  <strong>{cat.nome}</strong>
                  <span>
                    {cat.tipo === "receita"
                      ? "Categoria de receita"
                      : "Categoria de despesa"}
                  </span>
                </div>
              </div>

              <div className="cat-item-actions">
                <span
                  className={
                    cat.tipo === "receita"
                      ? "cat-badge receita"
                      : "cat-badge despesa"
                  }
                >
                  {cat.tipo === "receita" ? "Receita" : "Despesa"}
                </span>

                <button
                  className="cat-delete-btn"
                  onClick={() => excluirCategoria(uid, cat.id)}
                  type="button"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}
